/*
 * Prepares a report response (GET registrationReport/list; docs/director-reporting-prototype.md) for
 * the components. Rows keep the response's field names (participant.tshirtSize, insuranceCompany,
 * extras[].title ...), as the API sends them; components and utils read those names. Only real
 * conversions happen here: statusKey / status from status + isRefunded, medical and allergy lists
 * cleaned, the program object attached by programId, an answers lookup, defaults for missing fields,
 * and `id` (= rowId) for React keys.
 *
 * No money is computed here. `money`, `documents`, `answers` and `reminders` pass through as
 * received; utils/registrations reads them.
 */

/*
 * Five statuses, as the app has them. The camper's stored status 2 is Cancelled, 3 Refunded (refund
 * only: still attending), 4 Cancelled & refunded. The report sends 'cancelled' for 2 and 4, with
 * isRefunded on 4.
 */
const STATUS_LABELS = { active: 'Active', pending: 'Pending', refunded: 'Refunded', cancelled: 'Cancelled', cancelled_refunded: 'Cancelled & refunded' };

const statusKeyOf = row => (row.status === 'cancelled' && row.isRefunded ? 'cancelled_refunded' : row.status);

// Allergy and medical are stored as JSON-ish lists ('["Milk","Gluten"]'). Same intent as the
// booking page's removeStr (CamperDetail.js), which also drops the brackets of a one-item list.
export function cleanList(value) {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (!text) return '';
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.map(v => String(v).trim()).filter(Boolean).join(', ');
  } catch {
    // Not JSON: plain text.
  }
  return text.replace(/^\[|\]$/g, '').replace(/"/g, '').replace(/\s*,\s*/g, ', ').trim();
}

const contact = c => (c ? { name: c.name || '', phone: c.phone || '', email: c.email || '' } : null);

export function adaptReportRow(row, programsById) {
  const p = row.participant || {};
  const program = programsById.get(row.programId) || { programId: row.programId, programName: '', startDate: null, endDate: null, isDeleted: false };
  const answers = row.answers || [];
  const answersById = {};
  answers.forEach(a => {
    if (a && a.questionId) answersById[a.questionId] = a.answer || '';
  });
  const statusKey = statusKeyOf(row);

  return {
    ...row,
    id: row.rowId,
    bookingNumber: row.bookingNumber || '',
    parentUserId: row.parentUserId || '',
    program,
    participant: {
      ...p,
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      gender: p.gender || '',
      dob: p.dob || null,
      age: p.age ?? null,
      grade: p.grade || '',
      tshirtSize: p.tshirtSize || '',
      shortsSize: p.shortsSize || '',
      medical: cleanList(p.medical),
      allergy: cleanList(p.allergy),
      allergyDeadly: p.allergyDeadly ?? null,
      insuranceCompany: p.insuranceCompany || '',
      group: p.group || '',
      groupCode: p.groupCode || '',
    },
    address: { street: '', apt: '', city: '', state: '', zip: '', ...row.address },
    primaryGuardian: contact(row.primaryGuardian) || { name: '', phone: '', email: '' },
    secondaryGuardian: contact(row.secondaryGuardian),
    extras: row.extras || [],
    charges: row.charges || [],
    statusKey,
    status: STATUS_LABELS[statusKey] || row.status,
    isCancelled: Boolean(row.isCancelled),
    isRefunded: Boolean(row.isRefunded),
    statusNote: row.statusNote || '',
    answers,
    answersById,
    reminders: row.reminders || { documentsSentAt: null, paymentSentAt: null },
    noteCount: row.noteCount || 0,
  };
}

export function adaptReport(responseData) {
  const programs = responseData.programs || [];
  const programsById = new Map(programs.map(p => [p.programId, p]));
  return {
    camp: responseData.camp,
    range: responseData.range,
    programs,
    rows: (responseData.rows || []).map(r => adaptReportRow(r, programsById)),
  };
}

// The report with one row's fields replaced, e.g. a saved status note. Fields only the API derives
// (money, documents, status) are never patched here: a change to those means loading the report again.
export function patchReportRow(report, rowId, patch) {
  if (!report.rows.some(r => r.id === rowId)) return report;
  return { ...report, rows: report.rows.map(r => (r.id === rowId ? { ...r, ...patch } : r)) };
}
